import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { z } from 'zod';
import {
  SOURCE_RCS,
  SOURCE_SMS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import { baseQueryArgs, baseExtraOptions, rsosApi } from '../rsosApi';
import {
  csvToMessageRecords,
  type ConversationMessageRecord,
} from './csvToMessageRecords';

const TF911_REPORT_TEXT = 'Text_from_911_Report_Download';

const getTextFrom911BaseEndpoint = (orgName?: string, version = 'v1') => {
  return `${version}/tf911/psap/${orgName}`;
};

export const THIRTY_DAYS = 1000 * 60 * 60 * 24 * 30;
export const SEVEN_YEARS = 1000 * 60 * 60 * 24 * 365 * 7;
export const TWO_HOURS = 1000 * 60 * 60 * 2;

// session_type / status values mirror the enums in
// src/python3.10/rsos/libs/communicator/models.py.

// Session types carried by a phone-backed conversation.
const PHONE_SESSION_TYPES = [
  SOURCE_SMS,
  SOURCE_RCS,
  SOURCE_TCAPI,
  'sms_admin', // doesn't match websocket source SOURCE_ADMIN_SMS ('admin_sms')
] as const;

// The v2 endpoint unions both tables, so it also accepts interagency, the
// deprecated `convey`, and the legacy-only `default`.
const ALL_SESSION_TYPES = [
  ...PHONE_SESSION_TYPES,
  'interagency', // doesn't match websocket source SOURCE_INTERAGENCY_CHAT ('interagency_chat')
  'convey',
  'default',
] as const;

const CONVERSATION_STATUSES = ['active', 'inactive', 'pending'] as const;
// v2 unions legacy TF911Session rows (terminal = `inactive`) with modern
// TextSession rows, which serialize the terminal state as `ended` and can also
// be `timed_out`. All are accepted so a terminal TextSession row doesn't fail
// response validation.
const ALL_STATUSES = [...CONVERSATION_STATUSES, 'ended', 'timed_out'] as const;

/**
 * Runtime schema for a single row of the v2 paginated conversations response.
 * This (and the schemas/types derived from it) are the single source of truth
 * for conversation shapes — the TS types below are inferred from them so
 * validation and types can't drift. `phone_number` is nullable (it is `null`
 * for interagency rows) and the enums are intentionally lenient (see
 * ALL_SESSION_TYPES / ALL_STATUSES) so the endpoint's unioned/legacy rows pass.
 */
const PaginatedConversationSchema = z.object({
  conversation_id: z.string(),
  phone_number: z.string().nullable(),
  start_time: z.number(),
  duration: z.number().nullable(),
  session_type: z.enum(ALL_SESSION_TYPES),
  status: z.enum(ALL_STATUSES),
});

const PaginatedConversationsResponseSchema = z.object({
  conversations: z.array(PaginatedConversationSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

// The historic endpoint additionally returns retention info that the v2
// paginated endpoint omits.
const ConversationSchema = PaginatedConversationSchema.extend({
  time_until_expiration: z.number(),
});

// A single paginated conversation row (omits `time_until_expiration`).
export type PaginatedConversation = z.infer<typeof PaginatedConversationSchema>;
// A full conversation row, as returned by the historic endpoint.
export type Conversation = z.infer<typeof ConversationSchema>;
type PaginatedConversationsResponse = z.infer<
  typeof PaginatedConversationsResponseSchema
>;

/** Per-participant settings on a TalkJS conversation record. */
interface ConversationParticipant {
  access: 'ReadWrite' | 'Read' | 'None';
  notify: boolean;
}

/**
 * Response body of `getConversationMetadata`: the TalkJS conversation record
 * for a caller's ongoing session. The endpoint returns 204 (no body) when no
 * conversation exists, so callers should branch on `isSuccess` / presence
 * before reading these fields.
 */
export interface ConversationMetadata {
  conversation_id: string;
  // TF911 stores the session state under the `STATUS` custom field (e.g.
  // 'ACTIVE'). TalkJS custom values are always strings.
  custom: { STATUS: string };
  topicId: string | null;
  subject: string | null;
  createdAt: number;
  participants: Record<string, ConversationParticipant>;
}

interface ConversationMetadataArg {
  callerID: string;
  orgName?: string;
  // Not used by the query itself; forwarded via meta.arg.originalArgs and
  // read by the textFrom911 slice matchers.
  custom?: Record<string, unknown>;
}

interface CreateConversationArg {
  callerID: string | null;
  orgName?: string;
  startMessage?: string;
  languageCode?: string;
}

interface EndConversationArg {
  callerID: string;
  orgName?: string;
  // Optional pre-interpolated ending message body; sent as `end_message` when present.
  endMessage?: string;
  // Resolved smart-phrase values in endMessage to shield from translation.
  doNotTranslate?: string[];
}

interface HistoricConversationsArg {
  orgName?: string;
  startTime?: number;
  endTime?: number;
  status?: string;
  sessionTypes?: string;
}

interface PaginatedConversationsArg {
  orgName?: string;
  startTime?: number;
  endTime?: number;
  status?: string;
  sessionTypes?: string;
  page?: number;
  limit?: number;
  sortDir?: 'asc' | 'desc';
  searchTerm?: string;
}

interface DownloadConversationsArg {
  conversationIds: string[];
  orgName?: string;
  psapsDisplayName?: string;
}

/**
 * `getConversationMessages` mutation arg. The `query` only sends
 * `conversationId` + `orgName`; `callerId` and `source` are forwarded
 * via `meta.arg.originalArgs` so the `talkjsMessages` slice can stamp
 * each record on `matchFulfilled`.
 */
interface GetConversationMessagesArg {
  conversationId: string;
  orgName: string;
  callerId?: string;
  source?: string;
}

interface GetConversationMessagesResult {
  messages: ConversationMessageRecord[];
}

interface OutboundSMSArg {
  callerID: string | null;
  orgName?: string;
  message?: string;
  languageCode?: string;
  // Resolved smart-phrase values (agency name, contact info, ...) the backend
  // must not translate.
  doNotTranslate?: string[];
  // Not used by the query itself; some callers pass it through alongside the
  // create call (e.g. the start-conversation helper).
  custom?: Record<string, unknown>;
}

/**
 * Extends the rsosApi for text-from-911 endpoints.
 * Authorization header must be manually included to tell RTK which token to use
 * otherwise it will use the access token
 */
export const textFrom911Api = rsosApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Fetches a phone number's ongoing conversation metadata.
     * Returns 204 if none exists
     */
    getConversationMetadata: builder.query<
      ConversationMetadata,
      ConversationMetadataArg
    >({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(orgName)}/conversation/+${callerID}`,
        method: 'GET',
      }),
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Starts a conversation with the given phone number using `startMessage`
     */
    // TODO: should be builder.mutation
    createConversation: builder.query<
      { conversation_id?: string },
      CreateConversationArg
    >({
      query: ({ callerID, orgName, startMessage, languageCode }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(orgName)}/conversation/+${callerID}`,
        method: 'POST',
        body: JSON.stringify({
          start_message: startMessage,
          ...(languageCode ? { language_code: languageCode } : {}),
        }),
      }),
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Ends a conversation with the given phone number. Optionally includes an
     * already-interpolated endMessage.
     *
     * Returns 204 (no content) on success.
     */
    endConversation: builder.mutation<void, EndConversationArg>({
      query: ({ callerID, orgName, endMessage, doNotTranslate }) => {
        const body =
          endMessage !== undefined
            ? JSON.stringify({
                end_message: endMessage,
                ...(doNotTranslate?.length
                  ? { do_not_translate: doNotTranslate }
                  : {}),
              })
            : undefined;
        return {
          ...baseQueryArgs,
          url: `${getTextFrom911BaseEndpoint(orgName)}/conversation/+${callerID}`,
          method: 'PUT',
          ...(body !== undefined ? { body } : {}),
        };
      },
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Fetches conversations in a date range as a bare array. Used by the
     * `initializeHistoricConversations` thunk to bootstrap metadata for
     * currently-active conversations on app startup.
     */
    getHistoricConversations: builder.query<
      Conversation[],
      HistoricConversationsArg
    >({
      query: ({
        orgName,
        startTime = new Date().getTime() - THIRTY_DAYS,
        endTime = new Date().getTime(),
        status = '',
        sessionTypes = '',
      }) => {
        const params = new URLSearchParams({
          start_ms: String(startTime),
          end_ms: String(endTime),
        });

        if (status) params.set('status', status);
        if (sessionTypes) params.set('session_types', sessionTypes);

        return {
          ...baseQueryArgs,
          url: `${getTextFrom911BaseEndpoint(orgName)}/conversations?${params}`,
          method: 'GET',
        };
      },
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Fetches a paginated page of conversations with server-side phone-number
     * search and date sorting. Unlike `getHistoricConversations` (which returns
     * a bare array), this returns `{ conversations, total, page, limit }` and
     * supports arbitrarily large date ranges.
     */
    getPaginatedConversations: builder.query<
      PaginatedConversationsResponse,
      PaginatedConversationsArg
    >({
      query: ({
        orgName,
        startTime,
        endTime,
        status = '',
        sessionTypes = '',
        page = 1,
        limit = 50,
        sortDir = 'desc',
        searchTerm = '',
      }) => {
        const params = new URLSearchParams({
          start_ms: String(startTime),
          end_ms: String(endTime),
          page: String(page),
          limit: String(limit),
          sort_dir: sortDir,
        });

        if (status) params.set('status', status);
        if (sessionTypes) params.set('session_types', sessionTypes);
        if (searchTerm) params.set('search', searchTerm);

        return {
          ...baseQueryArgs,
          url: `${getTextFrom911BaseEndpoint(
            orgName,
            'v2',
          )}/conversations?${params}`,
          method: 'GET',
        };
      },
      // Validate the envelope at runtime. A schema mismatch throws, which RTK
      // Query surfaces to callers as an error (isError) rather than handing
      // back a malformed payload. The parsed result is the inferred
      // `PaginatedConversationsResponse`, so callers get the exact validated shape.
      transformResponse: (response: unknown) =>
        PaginatedConversationsResponseSchema.parse(response),
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    downloadConversations: builder.mutation<void, DownloadConversationsArg>({
      query: ({ conversationIds, orgName, psapsDisplayName }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(
          orgName,
        )}/conversation-history/download`,
        method: 'POST',
        body: {
          conversations: conversationIds,
        },
        responseHandler: async (response: Response) => {
          if (!response.ok) {
            return response.text();
          }

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(
            new Blob([blob], { type: 'text/csv' }),
          );

          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = `${psapsDisplayName}_${TF911_REPORT_TEXT}_${Date.now()}.csv`;
          a.click();

          // Clean up
          a.remove();
          window.URL.revokeObjectURL(blobUrl);
        },
        cache: 'no-cache',
      }),
    }),
    /**
     * Parses the CSV from `/conversation-history/download` into structured
     * message records instead of triggering a file download. Reuses the
     * exact endpoint (and therefore the auth + scoping) of
     * `downloadConversations` so the FE can build a search index without
     * any backend work.
     *
     * Caveats this inherits from the download endpoint:
     *   - Returns 403 if any requested id isn't an INACTIVE TF911 (or
     *     active text) session for the caller's authority. Callers should
     *     fetch one id at a time and skip 403s, otherwise a single bad id
     *     poisons the whole batch.
     *   - Cannot fetch live/active TF911 conversations (the BE's session
     *     lookup hardcodes `status=INACTIVE` for TF911). For active call
     *     queue coverage, the BE would need a sibling endpoint without
     *     that filter.
     *   - Cannot fetch collaborator/agency-share rooms (no TF911 session
     *     row exists for them).
     *
     * Resolves to `{ messages: Array<{ id, cleanedConversationId,
     * senderId, text, createdAt }> }`. The `cleanedConversationId` field
     * has no `tf911.` prefix (the BE strips it for CSV readability), so
     * the caller is responsible for mapping back to whichever id it sent.
     */
    getConversationMessages: builder.mutation<
      GetConversationMessagesResult,
      GetConversationMessagesArg
    >({
      query: ({ conversationId, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(
          orgName,
        )}/conversation-history/download`,
        method: 'POST',
        body: {
          conversations: [conversationId],
        },
        responseHandler: async (response: Response) => {
          // Non-2xx (most commonly the BE's 403 for active / non-owned
          // conversations) flows into RTK Query's `error.data` — return
          // the raw text so callers can log it if they want, but don't
          // try to parse it as CSV.
          if (!response.ok) {
            return await response.text();
          }
          const csvText = await response.text();
          return { messages: csvToMessageRecords(csvText) };
        },
        cache: 'no-cache',
      }),
      // This endpoint polls every 60s as a search-index warmer, so its
      // common failure modes are all expected and self-healing. Tag them
      // so rtkQueryErrorMiddleware skips the Sentry log:
      //   - 403: active / non-owned TF911 conversation.
      //   - FETCH_ERROR / TIMEOUT_ERROR: a transient network blip the
      //     next poll recovers from on its own.
      transformErrorResponse: (error: FetchBaseQueryError) =>
        error.status === 403 ||
        error.status === 'FETCH_ERROR' ||
        error.status === 'TIMEOUT_ERROR'
          ? { ...error, isExpectedError: true }
          : error,
      extraOptions: {
        ...baseExtraOptions,
        // Each failure (403, etc.) is expected and surfaced to the
        // caller; retrying won't help and just doubles the load on a
        // batch that already does N parallel requests.
        maxRetries: 0,
      },
    }),
    createConversationOutboundSMS: builder.mutation<
      { conversation_id?: string },
      OutboundSMSArg
    >({
      query: ({
        callerID,
        orgName,
        message,
        languageCode,
        doNotTranslate,
      }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(orgName)}/outbound-sms`,
        method: 'POST',
        body: JSON.stringify({
          caller_id: `+${callerID}`,
          message,
          ...(languageCode ? { language_code: languageCode } : {}),
          ...(doNotTranslate?.length
            ? { do_not_translate: doNotTranslate }
            : {}),
        }),
      }),
      // A 409 here means "a session already exists for this caller"; the
      // caller handles it by refetching conversation metadata. Tag it so
      // rtkQueryErrorMiddleware skips the toast + Sentry log.
      transformErrorResponse: (error: FetchBaseQueryError) =>
        typeof error.status === 'number' && error.status === 409
          ? { ...error, isExpectedError: true }
          : error,
      extraOptions: {
        ...baseExtraOptions,
        maxRetries: 0, // 409 error statuses trigger retry logic. explicitly do not retry
      },
    }),
    // exactly the same as outboundSMS mutation
    createConversationOutboundAgero: builder.mutation<
      { conversation_id?: string },
      OutboundSMSArg
    >({
      query: ({
        callerID,
        orgName,
        message,
        languageCode,
        doNotTranslate,
      }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(orgName)}/outbound-sms`,
        method: 'POST',
        body: JSON.stringify({
          caller_id: `+${callerID}`,
          message,
          ...(languageCode ? { language_code: languageCode } : {}),
          ...(doNotTranslate?.length
            ? { do_not_translate: doNotTranslate }
            : {}),
          // custom, TODO: look into passing in custom field
        }),
      }),
      extraOptions: {
        ...baseExtraOptions,
        maxRetries: 0, // 409 error statuses trigger retry logic. explicitly do not retry
      },
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useGetConversationMetadataQuery,
  useLazyGetConversationMetadataQuery,
  useCreateConversationQuery,
  useLazyCreateConversationQuery,
  useEndConversationMutation,
  useGetPaginatedConversationsQuery,
  useLazyGetPaginatedConversationsQuery,
  useDownloadConversationsMutation,
  useGetConversationMessagesMutation,
  useCreateConversationOutboundSMSMutation,
  useCreateConversationOutboundAgeroMutation,
} = textFrom911Api;
